import { spineLoader } from '@play-co/timestep-spine';
import type { DreamDriftNodeStatus } from 'src/game/dreamDrift/components/DreamDriftNode';
import type { ThemeAsset, ThemeDefinition } from 'src/game/theme/Theme';
import type {
  DreamDriftHomePetAssets,
  DreamDriftHudButtonAssets,
  DreamDriftMapConfig,
  DreamDriftThemeProvider,
} from '../dreamDrift.theme';

/** Root folder containing the Momotaro Dream Drift presentation assets. */
const themeFolder = 'assets/ui/dreamDrift/theme-momotaro';

/** Folder containing the Momotaro map slices, animations, and progress nodes. */
const mapFolder = `${themeFolder}/map`;

/** Folder containing the Momotaro home HUD button assets. */
const hudButtonFolder = `${themeFolder}/hudButton`;

/** Spine animation and timer image used by the Momotaro home HUD button. */
const hudButtonAssets: DreamDriftHudButtonAssets = {
  levelLabelLayout: { x: 120, y: 32, r: 0 },
  kind: 'spine',
  spine: {
    loadMethod: spineLoader,
    url: `${hudButtonFolder}/spine/dreamdrift.HUDicon`,
  },
  timer: `${hudButtonFolder}/timer.png`,
};

/** Static images used by the active Momotaro home-pet presentation. */
const homePetAssets: DreamDriftHomePetAssets = {
  bed: `${themeFolder}/bed.png`,
};

/** Source image filename associated with each Momotaro node status. */
const NODE_IMAGES: Record<DreamDriftNodeStatus, string> = {
  cleared: 'node_cleared.png',
  current: 'node_current.png',
  open: 'node_open.png',
};

/** Checkpoint artwork, with the same plate for current and upcoming checkpoints. */
const CHECKPOINT_IMAGES: Record<DreamDriftNodeStatus, string> = {
  cleared: 'checkpoint_cleared.png',
  current: 'checkpoint_current.png',
  open: 'checkpoint_current.png',
};

/** Background, chests, looping character placements, and progression path for Momotaro. */
const mapConfig = {
  dimensions: {
    width: 1080,
    height: 3452,
  },
  numSlices: 4,
  order: ['bg01.png', 'bg02.png', 'bg03.png', 'bg04.png'],
  chests: [
    // Overlap the lower-right edge of each checkpoint, one layer in front.
    { image: 'chest_bronze.png', x: 685, y: 2020, zIndex: 8 },
    { image: 'chest_silver.png', x: 750, y: 1260, zIndex: 5 },
    // Keep the final chest beside the summit node and behind the path.
    { image: 'chest_gold.png', x: 685, y: 380, zIndex: 0 },
  ],
  chests12: [
    // Keep each chest beside its checkpoint and the final chest on the summit.
    { image: 'chest_bronze.png', x: 685, y: 2020, zIndex: 10 },
    { image: 'chest_silver.png', x: 750, y: 1260, zIndex: 6 },
    { image: 'chest_gold.png', x: 685, y: 380, zIndex: 0 },
  ],
  spine: {
    path: `${mapFolder}/spine/dreamdrift.map`,
    animations: [
      { id: 'montaro', x: 835, y: 2755, scale: 0.9 },
      { id: 'bird', x: 620, y: 1700, scale: 1 },
      { id: 'monkey', x: 270, y: 1895, scale: 0.9 },
      { id: 'shiba', x: 850, y: 2020, scale: 1 },
      { id: 'oni_blue', x: 855, y: 1220, scale: 0.9, flipX: true },
      { id: 'oni_red', x: 260, y: 1210, scale: 0.85 },
    ],
  },
  nodes: [
    { x: 630, y: 2915 },
    { x: 550, y: 2715 },
    { x: 650, y: 2505 },
    { x: 525, y: 2080 },
    { x: 650, y: 1890 },
    { x: 460, y: 1740 },
    { x: 640, y: 1320 },
    { x: 590, y: 1110 },
    { x: 390, y: 960 },
    { x: 590, y: 580 },
  ],
  nodes12: [
    // Share the first island, checkpoints, and summit; fit four nodes on each middle island.
    { x: 630, y: 2915 },
    { x: 550, y: 2715 },
    { x: 650, y: 2505 },
    { x: 525, y: 2080 },
    { x: 440, y: 1930 },
    { x: 685, y: 1845 },
    { x: 445, y: 1725 },
    { x: 640, y: 1320 },
    { x: 460, y: 1210 },
    { x: 580, y: 1050 },
    { x: 380, y: 920 },
    { x: 590, y: 580 },
  ],
} satisfies DreamDriftMapConfig;

/**
 * Get every asset required to display the Momotaro Dream Drift theme.
 *
 * @returns Map images, parsed Spine data, and home-pet artwork for this theme.
 */
function getAssets(): ThemeAsset[] {
  // Preload the map artwork and shared Spine export before the popup opens.
  return [
    ...mapConfig.order.map(getMapImagePath),
    ...mapConfig.chests.map(({ image }) => getMapImagePath(image)),
    ...Object.values(NODE_IMAGES).map(getMapImagePath),
    ...[...new Set(Object.values(CHECKPOINT_IMAGES))].map(getMapImagePath),
    getMapImagePath('checkpoint_flag.png'),
    { url: mapConfig.spine.path, loadMethod: spineLoader },
    ...Object.values(homePetAssets),
  ];
}

/**
 * Get the static images used by the active Momotaro home-pet presentation.
 *
 * @returns Bed artwork displayed behind the sleeping pet.
 */
function getHomePetAssets(): DreamDriftHomePetAssets {
  // Return the named asset so home scenes can preload it independently.
  return homePetAssets;
}

/**
 * Get the animated icon and timer image used by the Momotaro home HUD button.
 *
 * @returns Spine asset description and timer background path for the active theme.
 */
function getHudButtonAssets(): DreamDriftHudButtonAssets {
  // Return the named assets so the button can load and position them separately.
  return hudButtonAssets;
}

/**
 * Resolve the Momotaro signup illustration used by the intro popup.
 *
 * @returns Complete themed asset path for the intro illustration.
 */
function getIntroImagePath(): string {
  // Keep the popup artwork aligned with the active presentation theme.
  return `${themeFolder}/intro.png`;
}

/** Get the existing Momotaro puzzle background path. */
function getPuzzleBackground(): string {
  // Retain the original puzzle art for Momotaro events.
  return 'assets/minigame/dreamDrift/theme-default/bg.png';
}

/**
 * Get Momotaro's plate and label layout in source pixels.
 * @param isCheckpoint Whether the plate marks a reward checkpoint.
 * @returns Bounds, label styling, and flag placement for this plate.
 */
function getNodeStyle(
  isCheckpoint: boolean,
): ReturnType<DreamDriftThemeProvider['getNodeStyle']> {
  // Preserve the existing Momotaro plate dimensions and brown numbering.
  return {
    width: isCheckpoint ? 222 : 173,
    height: isCheckpoint ? 193 : 152,
    label: {
      x: isCheckpoint ? 105 : 80,
      y: isCheckpoint ? 70 : 55,
      color: '#7B4625',
      r: 0,
    },
    flag: { x: 173, y: 8 },
  };
}

/**
 * Get the Momotaro map configuration.
 *
 * @returns Dimensions, background slices, chests, looping characters, and node positions.
 */
function getMapConfig(): DreamDriftMapConfig {
  // Return the immutable theme description consumed by the map component.
  return mapConfig;
}

/**
 * Resolve one configured map image filename to its themed asset path.
 *
 * @param image Filename of a map slice, chest, or progression node image.
 * @returns Complete asset path for the Momotaro map image.
 */
function getMapImagePath(image: string): string {
  // Prefix the theme map folder without exposing it to map components.
  return `${mapFolder}/${image}`;
}

/**
 * Get the Momotaro progress node image for one visual status.
 *
 * @param opts Progress status and checkpoint designation selecting the artwork.
 * @returns Complete themed asset path for the requested node status.
 */
function getNodeImage({
  status,
  isCheckpoint,
}: Parameters<DreamDriftThemeProvider['getNodeImage']>[0]): string {
  // Keep checkpoint artwork separate from the unchanged regular node states.
  const images = isCheckpoint ? CHECKPOINT_IMAGES : NODE_IMAGES;
  return getMapImagePath(images[status]);
}

/** Momotaro implementation of the Dream Drift presentation theme contract. */
export const dreamDriftThemeMomotaro: ThemeDefinition<DreamDriftThemeProvider> =
  {
    getAssets,
    getHomePetAssets,
    getHudButtonAssets,
    getIntroImagePath,
    getPuzzleBackground,
    getMapConfig,
    getMapImagePath,
    getNodeImage,
    getNodeStyle,
  };
